import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Link } from 'react-router-dom';
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputTextarea } from "primereact/inputtextarea";
import { useNavigate } from "react-router-dom";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";
import { useQuickBooksInvoiceSync, quickBooksSyncTemplate } from "../../../common/QuickBooksSyncColumn";
import { getLeaseInvoice, voidInvoice } from "../../../../utils/urls";
import Layout from "../../component/layout";
import { toastify } from '../../../../utils/toast';
import { getItem } from '../../../../utils/localStorage';

const InvoiceManagement = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);

  // Year-month filter
  const [selectedYearMonth, setSelectedYearMonth] = useState("all");
  const [availableYearMonths, setAvailableYearMonths] = useState([]);

  // Void Modal State
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [isVoiding, setIsVoiding] = useState(false);

  // User state
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [userId, setUserId] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    totalAmount: 0,
    totalPaid: 0,
    totalBalance: 0,
    invoiceCount: 0,
    paidCount: 0,
    unpaidCount: 0
  });

  const toast = useRef(null);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const { enabled: qbEnabled, statuses: qbStatuses } = useQuickBooksInvoiceSync(selectedFacilityId, invoices);

  // Fetch current user data
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        let parsedUser;

        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue;
        }

        if (parsedUser?.user) {
          const userObj = parsedUser.user;
          const extractedUserId = userObj._id || userObj.id || userObj.userId;

          setCurrentUser(userObj);
          setFullName(userObj.fullName || "");
          setRole(userObj.role || "");
          setUserId(extractedUserId);
        } else if (parsedUser) {
          const extractedUserId = parsedUser._id || parsedUser.id || parsedUser.userId;

          setCurrentUser(parsedUser);
          setFullName(parsedUser.fullName || "");
          setRole(parsedUser.role || "");
          setUserId(extractedUserId);
        }
      } catch (err) {
        console.error("Error getting current user:", err);
      }
    };

    getCurrentUser();
  }, []);

  // Extract available year-months from invoices
  useEffect(() => {
    if (invoices.length > 0) {
      const yearMonths = [...new Set(invoices.map(inv => inv.yearMonth).filter(Boolean))];
      const sortedYearMonths = yearMonths.sort((a, b) => {
        const dateA = new Date(a + "-01");
        const dateB = new Date(b + "-01");
        return dateB - dateA;
      });
      setAvailableYearMonths(sortedYearMonths);
    }
  }, [invoices]);

  // Format year-month for display
  const formatYearMonth = (yearMonth) => {
    if (!yearMonth) return "";
    const [year, month] = yearMonth.split("-");
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const handleYearMonthChange = (yearMonth) => {
    setSelectedYearMonth(yearMonth);
  };

  const fetchInvoices = async () => {
    setLoading(true);
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(`${getLeaseInvoice}/${facilityId}`, "GET");

      console.log("Lease invoice response:", response);

      if (response.success) {
        const invoiceData = Array.isArray(response.data) ? response.data :
          Array.isArray(response.data?.data) ? response.data.data : [];

        const transformedData = invoiceData.map(invoice => ({
          ...invoice,
          customerInfo: invoice.customerInfo || {
            fullName: 'No Tenant Assigned',
            firstName: '',
            lastName: ''
          }
        }));

        setInvoices(transformedData);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: response.message || "Failed to fetch lease invoices"
        });
        setInvoices([]);
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Error loading lease invoices"
      });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateDashboardData = (invoiceData) => {
    const filteredInvoices = invoiceData.filter((invoice) => {
      if (selectedYearMonth && selectedYearMonth !== "all") {
        return invoice.yearMonth === selectedYearMonth;
      }
      return true;
    });

    const activeInvoices = filteredInvoices.filter(invoice =>
      invoice.status?.toLowerCase() !== 'void'
    );

    const totalPaid = activeInvoices.reduce((sum, invoice) =>
      sum + (Number(invoice.amountPaid) || 0), 0
    );

    const totalAmount = activeInvoices.reduce((sum, invoice) =>
      sum + (Number(invoice.totalAmount) || 0), 0
    );

    const totalBalance = activeInvoices.reduce((sum, invoice) =>
      sum + (Number(invoice.balance) || 0), 0
    );

    const paidCount = activeInvoices.filter(invoice =>
      invoice.status?.toLowerCase() === 'paid' ||
      invoice.status?.toLowerCase() === 'fully paid'
    ).length;

    const unpaidCount = activeInvoices.filter(invoice =>
      invoice.status?.toLowerCase() === 'unpaid' ||
      invoice.status?.toLowerCase() === 'pending' ||
      invoice.status?.toLowerCase() === 'overdue'
    ).length;

    setDashboardData({
      totalAmount,
      totalPaid,
      totalBalance,
      invoiceCount: activeInvoices.length,
      paidCount,
      unpaidCount
    });
  };

  const handleViewInvoice = (invoiceId) => {
    navigate(`/facility/lease_management/invoice/${invoiceId}`, {
      state: { facilityId: selectedFacilityId }
    });
  };

  // Open void modal
  const openVoidModal = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setVoidReason("");
    setShowVoidModal(true);
  };

  // Close void modal
  const closeVoidModal = () => {
    setShowVoidModal(false);
    setSelectedInvoiceId(null);
    setVoidReason("");
  };

  // Handle void invoice submission
  const handleVoidInvoice = async () => {
    if (!voidReason.trim()) {
      toast.current.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please provide a reason for voiding this invoice",
        life: 3000,
      });
      return;
    }

    if (!fullName) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "User information not found. Please refresh and try again.",
        life: 3000,
      });
      return;
    }

    setIsVoiding(true);

    try {
      const payload = {
        reason: voidReason.trim(),
        userId: userId || currentUser?._id || currentUser?.id || "unknown",
        userName: fullName,
        userRole: role || "Unknown",
      };

      const response = await makeRequest2(
        `${voidInvoice}/${selectedFacilityId}/${selectedInvoiceId}`,
        "POST",
        payload
      );

      if (response.data || response.success) {
        toastify("Invoice successfully voided", "success");
        closeVoidModal();
        fetchInvoices();
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: response.error || response.message || "Failed to void invoice",
          life: 3000,
        });
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "An error occurred while voiding the invoice",
        life: 3000,
      });
    } finally {
      setIsVoiding(false);
    }
  };

  const formatDate = (value) => {
    return new Date(value).toLocaleDateString();
  };

  const notificationBodyTemplate = (rowData) => {
    const notifications = rowData.notificationsSent || {};
    const smsStatus = notifications.sms || false;
    const emailStatus = notifications.email || false;

    if (smsStatus && emailStatus) {
      return <span>Email & SMS</span>;
    } else if (emailStatus) {
      return <span>Email</span>;
    } else if (smsStatus) {
      return <span>SMS</span>;
    } else {
      return <span className="text-muted">None</span>;
    }
  };

  const statusTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status?.toLowerCase()) {
        case "paid":
        case "fully paid":
          return { color: "#4caf50", bgColor: "#e8f5e9" };
        case "partially paid":
          return { color: "#ff9800", bgColor: "#fff3e0" };
        case "pending":
        case "unpaid":
          return { color: "#ff9800", bgColor: "#fff3e0" };
        case "overdue":
          return { color: "#f44336", bgColor: "#ffebee" };
        case "void":
          return { color: "#9e9e9e", bgColor: "#f5f5f5" };
        default:
          return { color: "#2196f3", bgColor: "#e3f2fd" };
      }
    };

    const statusStyle = getSeverity(rowData.status);

    return (
      <span
        style={{
          fontWeight: "bold",
          color: statusStyle.color,
          padding: "5px 10px",
          borderRadius: "15px",
          backgroundColor: statusStyle.bgColor,
        }}
      >
        {rowData.status}
      </span>
    );
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-success p-ml-2"
          onClick={(e) => {
            e.stopPropagation();
            handleViewInvoice(rowData._id);
          }}
          tooltip="View Details"
        />
        {rowData.status?.toLowerCase() !== "void" &&
          rowData.status?.toLowerCase() !== "cancelled" &&
          rowData.status?.toLowerCase() !== "paid" && (
            <Button
              icon="ti ti-trash"
              className="p-button-rounded p-button-warning"
              tooltip="Void Invoice"
              onClick={(e) => {
                e.stopPropagation();
                openVoidModal(rowData._id);
              }}
              disabled={!fullName}
            />
          )}
      </div>
    );
  };

  const customerTemplate = (rowData) => {
    if (rowData.client) {
      const firstName = rowData.client.firstName || '';
      const lastName = rowData.client.lastName || '';
      if (firstName || lastName) {
        return `${firstName} ${lastName}`.trim();
      }
    }

    if (!rowData.customerInfo) {
      return 'No Tenant Assigned';
    }

    if (rowData.customerInfo.fullName &&
      rowData.customerInfo.fullName !== 'No Tenant Assigned' &&
      rowData.customerInfo.fullName !== 'No Tenant Found') {
      return rowData.customerInfo.fullName;
    }

    const firstName = rowData.customerInfo.firstName || '';
    const lastName = rowData.customerInfo.lastName || '';
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    return 'No Tenant Assigned';
  };

  const searchInvoices = (invoices) => {
    if (!searchKeyword) return invoices;

    return invoices.filter((invoice) => {
      const searchableText = [
        invoice.invoiceNumber,
        invoice.client?.firstName,
        invoice.client?.lastName,
        invoice.customerInfo?.fullName,
        `${invoice.customerInfo?.firstName || ''} ${invoice.customerInfo?.lastName || ''}`.trim(),
        invoice.status,
        invoice.unit?.name
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(searchKeyword.toLowerCase());
    });
  };

  const filterInvoicesByYearMonth = (invoices) => {
    if (!selectedYearMonth || selectedYearMonth === "all") return invoices;

    return invoices.filter((invoice) => invoice.yearMonth === selectedYearMonth);
  };

  const getFilteredInvoices = () => {
    let filtered = invoices;
    filtered = searchInvoices(filtered);
    filtered = filterInvoicesByYearMonth(filtered);
    return filtered;
  };

  const filteredInvoices = getFilteredInvoices();
  const allInvoices = filteredInvoices;
  const unpaidInvoices = filteredInvoices.filter(invoice =>
    invoice.status?.toLowerCase() === "unpaid");
  const paidInvoices = filteredInvoices.filter(invoice =>
    invoice.status?.toLowerCase() === "paid");
  const partiallyPaidInvoices = filteredInvoices.filter(invoice =>
    invoice.status?.toLowerCase() === "partially paid");
  const overdueInvoices = filteredInvoices.filter(invoice =>
    invoice.status?.toLowerCase() === "overdue");
  const voidInvoices = filteredInvoices.filter(invoice =>
    invoice.status?.toLowerCase() === "void");

  const getInvoicesByTab = (tabIndex) => {
    switch (tabIndex) {
      case 0: return allInvoices;
      case 1: return unpaidInvoices;
      case 2: return paidInvoices;
      case 3: return partiallyPaidInvoices;
      case 4: return overdueInvoices;
      case 5: return voidInvoices;
      default: return allInvoices;
    }
  };

  const handleTabClick = (index) => {
    setActiveTabIndex(index);
  };

  const clearSearch = () => {
    setSearchKeyword("");
  };

  const clearFilters = () => {
    setSelectedYearMonth("all");
  };

  const clearAll = () => {
    clearSearch();
    clearFilters();
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    if (invoices.length > 0) {
      calculateDashboardData(invoices);
    }
  }, [invoices, selectedYearMonth]);

  // Modal Footer
  const voidModalFooter = (
    <div>
      <Button
        label="Cancel"
        onClick={closeVoidModal}
        className="p-button-text btn btn-secondary me-2"
        type="button"
        disabled={isVoiding}
      />
      <Button
        label={isVoiding ? "Voiding..." : "Void Invoice"}
        onClick={handleVoidInvoice}
        className="p-button-text btn btn-danger me-2"
        type="submit"
        disabled={isVoiding || !voidReason.trim()}
        loading={isVoiding}
      />
    </div>
  );

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Lease Management</li>
                <li className="breadcrumb-item">Lease Invoices</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5>Lease Invoices</h5>
        </div>
        <div className="card-body">
          <Toast ref={toast} />

          {/* Void Invoice Modal */}
          <Dialog
            header="Void Invoice"
            visible={showVoidModal}
            style={{ width: '500px' }}
            onHide={closeVoidModal}
            footer={voidModalFooter}
            modal
            draggable={false}
          >
            <div className="p-fluid">
              <div className="mb-3">
                <p className="mb-2">
                  <strong>Warning:</strong> You are about to void this invoice. This action cannot be undone.
                </p>
                <p className="text-muted mb-3">
                  Voided by: <strong>{fullName}</strong> {role && `(${role})`}
                </p>
              </div>

              <div className="field">
                <label htmlFor="voidReason" className="font-bold">
                  Reason for Voiding <span className="text-danger">*</span>
                </label>
                <InputTextarea
                  id="voidReason"
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  rows={5}
                  placeholder="Please provide a detailed reason for voiding this invoice..."
                  className="w-100"
                  disabled={isVoiding}
                  autoFocus
                />
                {!voidReason.trim() && (
                  <small className="text-danger">This field is required</small>
                )}
              </div>
            </div>
          </Dialog>

          {/* Search and Filter Section */}
          <div className="row mb-3">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div className="d-flex col-md-8 col-xs-12 gap-2 mb-2 mb-md-0">
                      <div className="col-md-6">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by invoice number, customer name, unit, or status..."
                          value={searchKeyword}
                          onChange={(e) => setSearchKeyword(e.target.value)}
                        />
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-control"
                          value={selectedYearMonth}
                          onChange={(e) => handleYearMonthChange(e.target.value)}
                        >
                          <option value="all">All Months</option>
                          {availableYearMonths.map((yearMonth) => (
                            <option key={yearMonth} value={yearMonth}>
                              {formatYearMonth(yearMonth)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={clearAll}
                        disabled={!searchKeyword && selectedYearMonth === "all"}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card bg-light">
                <div className="card-body">
                  <h6 className="text-primary">Total Invoice Amount</h6>
                  <h3>KES {dashboardData.totalAmount.toLocaleString()}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card bg-light">
                <div className="card-body">
                  <h6 className="text-success">Total Paid Amount</h6>
                  <h3>KES {dashboardData.totalPaid.toLocaleString()}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card bg-light">
                <div className="card-body">
                  <h6 className="text-danger">Total Balance Due</h6>
                  <h3>KES {dashboardData.totalBalance.toLocaleString()}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <ul className="nav nav-tabs invoice-tab border-bottom" id="invoiceTab" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTabIndex === 0 ? 'active' : ''}`}
                  onClick={() => handleTabClick(0)}
                  type="button"
                  role="tab"
                >
                  <span className="d-flex align-items-center gap-2">
                    All <span className="avtar rounded-circle bg-light-primary">{allInvoices.length}</span>
                  </span>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTabIndex === 1 ? 'active' : ''}`}
                  onClick={() => handleTabClick(1)}
                  type="button"
                  role="tab"
                >
                  <span className="d-flex align-items-center gap-2">
                    Unpaid <span className="avtar rounded-circle bg-light-warning">{unpaidInvoices.length}</span>
                  </span>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTabIndex === 2 ? 'active' : ''}`}
                  onClick={() => handleTabClick(2)}
                  type="button"
                  role="tab"
                >
                  <span className="d-flex align-items-center gap-2">
                    Paid <span className="avtar rounded-circle bg-light-success">{paidInvoices.length}</span>
                  </span>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTabIndex === 3 ? 'active' : ''}`}
                  onClick={() => handleTabClick(3)}
                  type="button"
                  role="tab"
                >
                  <span className="d-flex align-items-center gap-2">
                    Partially Paid <span className="avtar rounded-circle bg-light-info">{partiallyPaidInvoices.length}</span>
                  </span>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTabIndex === 4 ? 'active' : ''}`}
                  onClick={() => handleTabClick(4)}
                  type="button"
                  role="tab"
                >
                  <span className="d-flex align-items-center gap-2">
                    Overdue <span className="avtar rounded-circle bg-light-danger">{overdueInvoices.length}</span>
                  </span>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTabIndex === 5 ? 'active' : ''}`}
                  onClick={() => handleTabClick(5)}
                  type="button"
                  role="tab"
                >
                  <span className="d-flex align-items-center gap-2">
                    Void <span className="avtar rounded-circle bg-light-secondary">{voidInvoices.length}</span>
                  </span>
                </button>
              </li>
            </ul>
          </div>

          {/* Invoice Table */}
          <Card>
            <DataTable
              value={getInvoicesByTab(activeTabIndex)}
              loading={loading}
              paginator
              rows={10}
              emptyMessage="No lease invoices found"
              className="p-datatable-sm"
              stripedRows
              selectionMode="single"
              sortField="issueDate"
              sortOrder={-1}
              responsiveLayout="scroll"
            >
              <Column
                field="invoiceNumber"
                header="Invoice #"
                style={{ minWidth: "150px" }}
              />
              <Column
                field="customerInfo.fullName"
                header="Customer"
                body={customerTemplate}
                sortable
                style={{ minWidth: "200px" }}
              />
              <Column
                field="unit.name"
                header="Unit"
                style={{ minWidth: "150px" }}
              />
              <Column
                field="totalAmount"
                header="Amount"
                style={{ minWidth: "120px" }}
                body={(rowData) => {
                  let currencyCode;
                  if (rowData.currency && rowData.currency.code) {
                    currencyCode = rowData.currency.code;
                  } else if (rowData.paymentDetails && rowData.paymentDetails.currency) {
                    currencyCode = rowData.paymentDetails.currency;
                  } else {
                    currencyCode = 'KES';
                  }
                  return `${currencyCode} ${rowData.totalAmount.toLocaleString()}`;
                }}
              />
              <Column
                field="issueDate"
                header="Issue Date"
                body={(rowData) => formatDate(rowData.issueDate)}
                style={{ minWidth: "120px" }}
              />
              <Column field="yearMonth" header="Month" sortable />
              <Column
                header="Notification Sent"
                body={notificationBodyTemplate}
                style={{ width: '120px' }}
              />
              <Column
                field="dueDate"
                header="Due Date"
                body={(rowData) => formatDate(rowData.dueDate)}
                style={{ minWidth: "120px" }}
              />
              <Column
                field="status"
                header="Status"
                body={statusTemplate}
                style={{ minWidth: "120px" }}
              />
               <Column
                field="digitax.synced"
                header="Digitax sync"
                style={{ minWidth: "120px" }}
              />
              {qbEnabled && (
                <Column
                  header="QuickBooks"
                  body={quickBooksSyncTemplate(qbStatuses)}
                  style={{ minWidth: "130px" }}
                />
              )}
              <Column
                header="Actions"
                body={actionTemplate}
                style={{ minWidth: "100px" }}
              />
            </DataTable>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default InvoiceManagement;